import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { logger } from 'src/lib/logger';
import api, { clearSessionToken, setSessionToken } from 'src/lib/api/session';
import { authStorage } from 'src/lib/auth/authStorage';
import { AxiosError } from 'axios';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  token: string;
  refresh_token?: string;
  user: User;
}

interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

interface RegisterResponse {
  id: string;
  email: string;
  name: string;
  message: string;
}

export interface User {
  id: string;
  name?: string;
  email?: string;
  picture?: string;
  survey_completed_at?: string;
}

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

export const fetchMe = async (): Promise<User> => {
  logger.debug('[Auth] Fetching user data from /v1/user');
  try {
    const res = await api.get('/v1/user');
    logger.info('[Auth] Successfully fetched user data', {
      userId: res.data.id,
      email: res.data.email
    });
    return res.data;
  } catch (error) {
    logger.error('[Auth] Failed to fetch user data', { error });
    throw error;
  }
};

export const signOutRequest = async (): Promise<void> => {
  logger.info('[Auth] Sending sign out request to /v1/oauth2/user/sessions');
  try {
    await api.delete('/v1/oauth2/user/sessions');
    logger.info('[Auth] Sign out request completed successfully');
  } catch (error) {
    logger.warn('[Auth] Sign out request failed', { error });
    throw error;
  }
};

export const useMeQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchMe,
    enabled,
    retry: (failureCount, error: AxiosError) => {
      if (error?.response?.status === 401) {
        return false;
      }

      if (!error?.response) {
        return false;
      }

      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }

      if (error?.response?.status >= 500) {
        return failureCount < 2;
      }

      return false;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    throwOnError: false,
  });
};

const clearUserLocalStorage = (): void => {
  const itemsToKeep = [
    'vite-ui-theme',
  ];

  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) keys.push(key);
  }

  keys.forEach((key) => {
    if (!itemsToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  logger.info('[Auth] Cleared user data from localStorage', {
    itemsCleared: keys.filter((k) => !itemsToKeep.includes(k)).length,
    itemsKept: itemsToKeep.length,
  });
};

export const useSignOutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signOutRequest,
    onMutate: async () => {
      logger.info('[Auth] Starting logout process');

      const cancelledQueries = await queryClient.cancelQueries();
      logger.debug('[Auth] Cancelled ongoing queries', { count: cancelledQueries });

      clearSessionToken();

      logger.info('[Auth] All queries cancelled and token cleared before logout request');
    },
    onSuccess: () => {
      logger.info('[Auth] Logout API call successful, clearing local data');

      queryClient.clear();

      clearUserLocalStorage();

      logger.info('[Auth] Sign out successful - all data cleared');
    },
    onError: (error) => {
      logger.error('[Auth] Sign out API error, forcing local cleanup', { error });

      clearSessionToken();

      queryClient.clear();

      clearUserLocalStorage();

      logger.info('[Auth] Sign out completed with errors - local data cleared anyway');
    },
  });
};

export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LoginRequest): Promise<LoginResponse> => {
      logger.debug('[Auth] Attempting login', { email: data.email });
      const response = await api.post<LoginResponse>('/v1/sessions', data);
      return response.data;
    },
    onSuccess: (data) => {
      logger.info('[Auth] Login successful', { userId: data.user.id });

      if (data.token) {
        setSessionToken(data.token);
      }

      if (data.refresh_token) {
        authStorage.saveRefreshToken(data.refresh_token);
      }

      void queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error: AxiosError) => {
      logger.error('[Auth] Login failed', { error });
    },
  });
};

export const useRegisterMutation = () => {
  return useMutation({
    mutationFn: async (data: RegisterRequest): Promise<RegisterResponse> => {
      logger.debug('[Auth] Attempting registration', { email: data.email });
      const response = await api.post<RegisterResponse>('/v1/users', data);
      return response.data;
    },
    onSuccess: (data) => {
      logger.info('[Auth] Registration successful', { userId: data.id, email: data.email });
    },
    onError: (error: AxiosError) => {
      logger.error('[Auth] Registration failed', { error });
    },
  });
};

interface SurveyRequest {
  use_case: string;
  hardware_readiness: string;
  discovery_source: string;
}

export const useSaveSurveyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SurveyRequest) => {
      logger.debug('[Auth] Saving survey');
      const response = await api.put('/v1/user/survey', data);
      return response.data;
    },
    onSuccess: () => {
      logger.info('[Auth] Survey saved successfully');
      void queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error: AxiosError) => {
      logger.error('[Auth] Survey save failed', { error });
    },
  });
};

interface VerifyEmailResponse {
  message: string;
  token?: string;
}

export const useVerifyEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string): Promise<VerifyEmailResponse> => {
      logger.debug('[Auth] Verifying email');
      const response = await api.put<VerifyEmailResponse>(`/v1/users/email-confirmation/${token}`);
      return response.data;
    },
    onSuccess: (data) => {
      logger.info('[Auth] Email verified successfully', { hasToken: !!data.token });

      if (data.token) {
        setSessionToken(data.token);
      }

      void queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error: AxiosError) => {
      logger.error('[Auth] Email verification failed', { error });
    },
  });
};

export const useAuthState = () => {
  const { data: user, isLoading, isError, error, refetch } = useMeQuery();
  const signOutMutation = useSignOutMutation();

  const isNetworkError = isError && !error?.response;
  const isServerDown =
    isNetworkError &&
    (error?.code === 'ERR_CONNECTION_REFUSED' ||
      error?.code === 'ERR_NETWORK' ||
      error?.message?.includes('ERR_CONNECTION_REFUSED'));

  return {
    user: user || null,
    isAuthenticated: Boolean(user && !isError),
    isLoading: isLoading && !isServerDown,
    isError: isError && !isNetworkError,
    isServerDown,
    signOut: signOutMutation.mutateAsync,
    isSigningOut: signOutMutation.isPending,
    refetchUser: refetch,
  };
};
